import { FORM_ERROR } from 'final-form'
import React from 'react'

import Spinner from '@cinch/oxd-components/loading-spinner/src'
import { GoogleMapsService } from '@cinch/oxd-utils/requests/src'

import Form from './components/form/form.component'

import * as styles from './geocode.module.css'

export enum GeocoderStatus {
  ERROR = 'ERROR',
  INVALID_REQUEST = 'INVALID_REQUEST',
  OK = 'OK',
  OVER_QUERY_LIMIT = 'OVER_QUERY_LIMIT',
  REQUEST_DENIED = 'REQUEST_DENIED',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  ZERO_RESULTS = 'ZERO_RESULTS',
}

enum AddressTypes {
  POSTCODE = 'postal_code',
  LOCALITY = 'locality',
  POSTAL_TOWN = 'postal_town',
}

export type GeoCodeParams = Pick<Cinch.UserProfileRequest, 'location' | 'town' | 'postcode' | 'latitude' | 'longitude'>

export interface GeocodeProps {
  onChange: (update: GeoCodeParams) => void
  onCancel?: () => void
}

const FORM_KEY = 'location'

const Geocode: React.FC<GeocodeProps> = ({ onChange, onCancel }) => {
  const [fetching, setFetching] = React.useState(false)

  const containsPostcode = (address: google.maps.GeocoderAddressComponent): boolean =>
    address.types.includes(AddressTypes.POSTCODE)
  const containsTownName = (address: google.maps.GeocoderAddressComponent): boolean =>
    address.types.includes(AddressTypes.POSTAL_TOWN)
  const containsLocality = (address: google.maps.GeocoderAddressComponent): boolean =>
    address.types.includes(AddressTypes.LOCALITY)

  const onSubmit = async (values: { [FORM_KEY]: string }) => {
    setFetching(true)

    try {
      const response = await GoogleMapsService.geocode(values[FORM_KEY])
      setFetching(false)

      const status = (response.data.status as unknown) as string

      if (status !== GeocoderStatus.OK) {
        if (status === GeocoderStatus.ZERO_RESULTS) {
          return { [FORM_KEY]: 'No results found' }
        }

        return { [FORM_KEY]: status }
      }

      const { geometry, formatted_address: location, address_components: addresses } = response.data.results[0]
      const { lat: latitude, lng: longitude } = (geometry.location as unknown) as google.maps.LatLngLiteral

      let town = ''
      let postcode = ''

      addresses.forEach((address) => {
        // Use locality over town name as this is a more accurate location
        if (!town && containsLocality(address)) {
          town = address.long_name
        } else if (!town && containsTownName(address)) {
          town = address.long_name
        }

        if (containsPostcode(address)) {
          postcode = address.long_name
        }
      })

      const returnValues: GeoCodeParams = { location, town, postcode, latitude, longitude }

      onChange(returnValues)
    } catch (error) {
      setFetching(false)

      return { [FORM_ERROR]: error.message }
    }
  }

  return (
    <div className={styles.geocode}>
      {fetching && (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      )}
      <Form onSubmit={onSubmit} onCancel={onCancel} />
    </div>
  )
}

export default Geocode
